import { Injectable } from '@angular/core';
import {
  ILuminaria,
  ICreateLuminaria,
  IQueryParam,
  IUpdateLuminaria,
  IAlerta,
  IEstablecimiento,
  ILote,
  IReporteLuminaria,
  IDispositivoLuminaria,
} from 'modelos/src';
import { BehaviorSubject, Observable } from 'rxjs';
import { apiBase } from 'src/environments/environment';
import { HelperService } from '../../../auxiliares/helper.service';
import { HttpService } from '../../../auxiliares/http.service';
import { DomSanitizer } from '@angular/platform-browser';

const api = apiBase;

export interface IEstadoLuminaria {
  icono?: string;
  ultimoEstado?: string;
  modo?: string;
  alimentacion?: string;
  consumo?: string;
  consumoAcumulado?: string;
  iconoMapa?: string;
}

@Injectable({
  providedIn: 'root',
})
export class LuminariasService {
  public filtrosActivosSubject = new BehaviorSubject<any[]>([]);
  filtrosActivos$ = this.filtrosActivosSubject.asObservable();

  private partidos: IEstablecimiento[] = [];
  private localidades: ILote[] = [];

  constructor(
    private http: HttpService,
    private sanitizer: DomSanitizer,
  ) {}

  // Luminarias
  public getById(id: string): Observable<ILuminaria> {
    return this.http.get(`${api}/luminarias/${id}`);
  }

  public getFiltered(queryParams?: IQueryParam): Observable<{
    data: ILuminaria[];
    total: number;
  }> {
    const params = HelperService.getQueryParams(queryParams);
    return this.http.get(`${api}/luminarias`, { params });
  }

  public create(data: ICreateLuminaria): Observable<ILuminaria> {
    return this.http.post(`${api}/luminarias`, data);
  }

  public update(id: string, data: IUpdateLuminaria): Observable<ILuminaria> {
    return this.http.put(`${api}/luminarias/${id}`, data);
  }
  public delete(id: string): Observable<ILuminaria> {
    return this.http.delete(`${api}/luminarias/${id}`);
  }

  // Métodos para actualizar los datos de partidos y localidades
  public actualizarPartidos(partidos: IEstablecimiento[]): void {
    this.partidos = partidos;
  }

  public actualizarLocalidades(localidades: ILote[]): void {
    this.localidades = localidades;
  }

  // Reportes
  public async exportarReportes(
    queryParams?: IQueryParam,
    filename = 'reportes.xlsx',
  ): Promise<void> {
    const params = HelperService.getQueryParams(queryParams);
    await this.http.getFile(
      `${api}/reportes/exportar/luminaria`,
      filename,
      params,
    );
  }

  // Alertas
  public delAlerta(id: string): Observable<IAlerta> {
    return this.http.delete(`${api}/alertas/${id}`);
  }

  // helper
  actualizarFiltros(filtros: string[]): void {
    this.filtrosActivosSubject.next(filtros);
  }

  estaFiltroActivo(filtro: string): boolean {
    return this.filtrosActivosSubject.getValue().includes(filtro);
  }

  activarFiltro(filtro: string): void {
    const filtros = this.filtrosActivosSubject.getValue();
    if (!filtros.includes(filtro)) {
      this.filtrosActivosSubject.next([...filtros, filtro]);
    }
  }

  desactivarFiltro(filtro: string): void {
    const filtros = this.filtrosActivosSubject.getValue();
    this.filtrosActivosSubject.next(filtros.filter((f) => f !== filtro));
  }

  public getFiltroNombre(filtro: string): string {
    const partido = this.partidos?.find((part) => part._id === filtro);
    const localidad = this.localidades?.find((loc) => loc._id === filtro);

    if (partido) {
      return partido.nombre;
    } else if (localidad) {
      return localidad.nombre;
    } else {
      return filtro;
    }
  }

  private esEstado(filtro: string): boolean {
    // Implementación para determinar si el filtro es un estado
    return [
      'Encendido',
      'Encendido Error',
      'Apagado',
      'Apagado Error',
      'Indeterminado',
    ].includes(filtro);
  }

  private esModo(filtro: string): boolean {
    // Implementación para determinar si el filtro es un modo
    return ['Manual', 'Fotocélula', 'Calendario', 'GPS'].includes(filtro);
  }

  private esPartido(filtro: string): boolean {
    return this.partidos.some((partido) => partido._id === filtro);
  }

  private esLocalidad(filtro: string): boolean {
    return this.localidades.some((localidad) => localidad._id === filtro);
  }

  public getLocalidadesInPartidosFilters(): ILote[] {
    const filtrosActivos = this.filtrosActivosSubject.getValue();
    const filtrosPartido = filtrosActivos.filter((filtro) =>
      this.esPartido(filtro),
    );
    if (filtrosPartido.length === 0) {
      return this.localidades;
    }
    return this.localidades.filter((localidad) =>
      filtrosActivos.includes(localidad.idEstablecimiento),
    );
  }

  public determinarModoLuminaria(luminaria: ILuminaria): string {
    if (!luminaria.dispositivo) {
      return 'Manual';
    }

    const { config } = luminaria.dispositivo;
    if (!config) {
      return 'Manual';
    }

    if (
      config.modoCalendarioActivo !== undefined &&
      config.modoCalendarioActivo === true
    ) {
      return 'Calendario';
    }

    if (config.modoGPSActivo !== undefined && config.modoGPSActivo === true) {
      return 'GPS';
    }

    if (config.modoLDRActivo !== undefined && config.modoLDRActivo === true) {
      return 'Fotocélula';
    }

    return 'Manual';
  }

  public determinarEstadoLuminaria(luminaria: ILuminaria): IEstadoLuminaria {
    const estadoLuminaria: IEstadoLuminaria = {
      icono: 'assets/svg/luminarias/indeterminado.svg',
      ultimoEstado: 'Indeterminado',
      alimentacion: 'Consumo normal',
      iconoMapa: 'assets/svg/luminarias/map_indeterminada.webp',
      consumo: '0W',
      consumoAcumulado: '0 Kw/h',
    };

    if (luminaria.dispositivo === undefined) {
      return estadoLuminaria;
    }

    const config = (luminaria.dispositivo?.config ??
      {}) as IDispositivoLuminaria;
    const reporte =
      luminaria.dispositivo?.ultimoReporte?.reporte ??
      ({} as IReporteLuminaria);
    const { turnOnOffStatus, alarma, activePowerTotal } = config;
    const { fechaUltimaComunicacion } = luminaria.dispositivo;
    const { voltage, current } = reporte;
    // Umbral de tiempo para considerar
    let umbralTiempo = 30 * 60 * 1000; // 30 minutos
    if (
      luminaria.dispositivo?.config?.intervalGeneral &&
      luminaria.dispositivo?.config?.intervalGeneral > 0
    ) {
      const intervaloX5 = luminaria.dispositivo?.config?.intervalGeneral * 5;
      umbralTiempo = intervaloX5 * 60 * 1000;
    }
    const tiempoActual = Date.now();

    if (
      !fechaUltimaComunicacion ||
      tiempoActual - new Date(fechaUltimaComunicacion).getTime() > umbralTiempo
    ) {
      return estadoLuminaria;
    }

    const consumo = voltage! * current!;

    // Encendida
    if (turnOnOffStatus) {
      if (!alarma || alarma === '') {
        estadoLuminaria.ultimoEstado = 'Encendido';
        estadoLuminaria.iconoMapa = 'assets/svg/luminarias/map_encendida.webp';
        estadoLuminaria.icono = 'assets/svg/luminarias/encendida.svg';
        estadoLuminaria.consumo = consumo.toFixed(2) + ' w';
        estadoLuminaria.consumoAcumulado = (activePowerTotal || 0) + ' Kw/h';
      } else {
        estadoLuminaria.ultimoEstado = 'Encendido Error';
        estadoLuminaria.icono = 'assets/svg/luminarias/encendidaErr.svg';
        estadoLuminaria.iconoMapa =
          'assets/svg/luminarias/map_encendidaErr.webp';
        estadoLuminaria.consumo = consumo.toFixed(2) + ' w';
        estadoLuminaria.consumoAcumulado = (activePowerTotal || 0) + ' Kw/h';
        estadoLuminaria.alimentacion = alarma;
      }
      // Apagada
    } else {
      if (!alarma || alarma === '') {
        estadoLuminaria.ultimoEstado = 'Apagado';
        estadoLuminaria.icono = 'assets/svg/luminarias/apagada.svg';
        estadoLuminaria.iconoMapa = 'assets/svg/luminarias/map_apagada.webp';
        estadoLuminaria.consumo = consumo.toFixed(2) + ' w';
        estadoLuminaria.consumoAcumulado = (activePowerTotal || 0) + ' Kw/h';
      } else {
        estadoLuminaria.ultimoEstado = 'Apagado Error';
        estadoLuminaria.icono = 'assets/svg/luminarias/apagadaErr.svg';
        estadoLuminaria.iconoMapa = 'assets/svg/luminarias/map_apagadaErr.webp';
        estadoLuminaria.consumo = consumo.toFixed(2) + ' w';
        estadoLuminaria.consumoAcumulado = (activePowerTotal || 0) + ' Kw/h';
        estadoLuminaria.alimentacion = alarma;
      }
    }

    return estadoLuminaria;
  }

  obtenerLuminariasFiltradas(luminarias: ILuminaria[]): ILuminaria[] {
    const filtrosActivos = this.filtrosActivosSubject.getValue();
    if (!luminarias) {
      return [];
    }
    if (filtrosActivos.length === 0) {
      return luminarias;
    }

    // Separar los filtros en estado, modo y grupo
    const filtrosEstado = filtrosActivos.filter((filtro) =>
      this.esEstado(filtro),
    );
    const filtrosModo = filtrosActivos.filter((filtro) => this.esModo(filtro));
    const filtrosPartido = filtrosActivos.filter((filtro) =>
      this.esPartido(filtro),
    );
    const filtrosLocalidad = filtrosActivos.filter((filtro) =>
      this.esLocalidad(filtro),
    );

    return luminarias.filter((luminaria: ILuminaria) => {
      const estado = this.determinarEstadoLuminaria(luminaria).ultimoEstado;
      const modo = this.determinarModoLuminaria(luminaria);
      const idpartidoLuminaria = luminaria.idEstablecimiento;
      const idLocalidadLuminaria = luminaria.idLote;

      if (!estado || !modo) {
        return false;
      }

      // Verificar que la luminaria cumpla con los filtros de estado (inclusivo)
      const cumpleEstado =
        filtrosEstado.length === 0 || filtrosEstado.includes(estado);

      // Verificar que la luminaria cumpla con los filtros de modo (inclusivo)
      const cumpleModo = filtrosModo.length === 0 || filtrosModo.includes(modo);

      // Verificar que la luminaria cumpla con los filtros de grupo (exclusivo)
      let cumplePartido = filtrosPartido.length === 0;
      if (filtrosPartido.length === 1) {
        cumplePartido = idpartidoLuminaria
          ? filtrosPartido.includes(idpartidoLuminaria)
          : false;
      } else if (filtrosPartido.length > 1) {
        cumplePartido = filtrosPartido.some((grupo) =>
          idpartidoLuminaria ? grupo.includes(idpartidoLuminaria) : false,
        );
      } else {
        cumplePartido = true;
      }

      // Verificar que la luminaria cumpla con los filtros de localidad (exclusivo)
      let cumpleLocalidad = filtrosLocalidad.length === 0;
      if (filtrosLocalidad.length === 1) {
        cumpleLocalidad = idLocalidadLuminaria
          ? filtrosLocalidad.includes(idLocalidadLuminaria)
          : false;
      } else if (filtrosLocalidad.length > 1) {
        cumpleLocalidad = filtrosLocalidad.some((localidad) =>
          idLocalidadLuminaria
            ? localidad.includes(idLocalidadLuminaria)
            : false,
        );
      } else {
        cumpleLocalidad = true;
      }

      return cumpleEstado && cumpleModo && cumplePartido && cumpleLocalidad;
    });
  }

  // datos de luminarias
  public getConsumoMaximo(luminaria: ILuminaria): string {
    if (
      luminaria &&
      luminaria.dispositivo?.ultimoReporte?.reporte?.voltage &&
      luminaria.dispositivo?.ultimoReporte?.reporte?.current
    ) {
      const voltage = luminaria.dispositivo.ultimoReporte.reporte.voltage;
      const current = luminaria.dispositivo.ultimoReporte.reporte.current;
      const consumo = voltage * current;
      return consumo.toFixed(2) + 'W';
    } else {
      return '- 0W';
    }
  }

  // Asegurarse de que las horas tengan el formato correcto con segundos
  public agregarSegundos = (time: string): string => {
    if (time.length === 5) {
      return `${time}:00`;
    }
    return time;
  };

  private convertMsToTime(duration: number): string {
    // const milliseconds = Math.floor((duration % 1000) / 100);
    const seconds = Math.floor((duration / 1000) % 60),
      minutes = Math.floor((duration / (1000 * 60)) % 60),
      hours = Math.floor((duration / (1000 * 60 * 60)) % 24);

    const hoursStr = hours < 10 ? '0' + hours : hours;
    const minutesStr = minutes < 10 ? '0' + minutes : minutes;
    const secondsStr = seconds < 10 ? '0' + seconds : seconds;

    return `${hoursStr}:${minutesStr}:${secondsStr}`;
  }

  public getTiempoEncendido(luminaria: ILuminaria): string {
    if (luminaria.dispositivo?.config?.deviceWorkingTime) {
      return this.convertMsToTime(
        luminaria.dispositivo.config.deviceWorkingTime,
      );
    } else {
      return '00:00:00';
    }
  }

  public frecReporte(
    deveui: string,
    frecuenciaPeriodico: number,
    frecuenciaEnergia: number,
  ) {
    return this.http.post(`${api}/comandos/luminarias/frecuenciaReporte`, {
      deveui: deveui,
      frecuenciaPeriodico: frecuenciaPeriodico,
      frecuenciaEnergia: frecuenciaEnergia,
    });
  }

  public configHour(deveui: string) {
    return this.http.post(`${api}/comandos/luminarias/setearHoraActual`, {
      deveui: deveui,
    });
  }

  public encender(deveui: string, dimmer: number) {
    return this.http.post(`${api}/comandos/luminarias/encender`, {
      deveui: deveui,
      dimmer: dimmer,
    });
  }

  public apagar(deveui: string) {
    return this.http.post(`${api}/comandos/luminarias/apagar`, {
      deveui: deveui,
    });
  }

  public dimerizar(deveui: string, dimmer: number) {
    return this.http.post(`${api}/comandos/luminarias/dimerizar`, {
      deveui: deveui,
      dimmer: dimmer,
    });
  }

  public habilitarGps(deveui: string) {
    return this.http.post(`${api}/comandos/luminarias/habilitarGPS`, {
      deveui: deveui,
    });
  }

  public deshabilitarGps(deveui: string) {
    return this.http.post(`${api}/comandos/luminarias/deshabilitarGPS`, {
      deveui: deveui,
    });
  }

  public consultGPS(deveui: string) {
    return this.http.post(`${api}/comandos/luminarias/consultarGPS`, {
      deveui: deveui,
    });
  }

  public habilitarLDR(deveui: string) {
    return this.http.post(`${api}/comandos/luminarias/habilitarModoLDR`, {
      deveui: deveui,
    });
  }

  public deshabilitarLDR(deveui: string) {
    return this.http.post(`${api}/comandos/luminarias/deshabilitarModoLDR`, {
      deveui: deveui,
    });
  }

  public habilitarCalendario(deveui: string) {
    return this.http.post(
      `${api}/comandos/luminarias/habilitarModoCalendarioYGPS`,
      {
        deveui: deveui,
        modoGps: false,
        modoCalendario: true,
      },
    );
  }

  public deshabilitarCalendario(deveui: string) {
    return this.http.post(
      `${api}/comandos/luminarias/habilitarModoCalendarioYGPS`,
      {
        deveui: deveui,
        modoGps: false,
        modoCalendario: false,
      },
    );
  }

  public habilitarModoGps(deveui: string) {
    return this.http.post(
      `${api}/comandos/luminarias/habilitarModoCalendarioYGPS`,
      {
        deveui: deveui,
        modoGps: true,
        modoCalendario: false,
      },
    );
  }

  public deshabilitarModoGps(deveui: string) {
    return this.http.post(
      `${api}/comandos/luminarias/habilitarModoCalendarioYGPS`,
      {
        deveui: deveui,
        modoGps: false,
        modoCalendario: false,
      },
    );
  }

  public setearOffsetGPS(
    deveui: string,
    offsetAmanecer: number,
    offsetAtardecer: number,
  ) {
    return this.http.post(`${api}/comandos/luminarias/setearOffsetGPS`, {
      deveui: deveui,
      offsetAmanecer: offsetAmanecer,
      offsetAtardecer: offsetAtardecer,
    });
  }

  public setearMargenesLDR(
    deveui: string,
    valorEncendido: number,
    valorApagado: number,
  ) {
    return this.http.post(`${api}/comandos/luminarias/setearMargenesLDR`, {
      deveui: deveui,
      valorEncendido: valorEncendido,
      valorApagado: valorApagado,
    });
  }

  public setearDelayModoLDR(deveui: string, delay: number) {
    return this.http.post(`${api}/comandos/luminarias/setearDelayModoLDR`, {
      deveui: deveui,
      delay: delay,
    });
  }

  public setearEstrategiaModoCalendario(
    deveui: string,
    estrategia: {
      numeroEstrategia: number;
      habilitada: boolean;
      horaDimmer1: string;
      dimmer1: number;
      horaDimmer2: string;
      dimmer2: number;
    },
  ) {
    return this.http.post(
      `${api}/comandos/luminarias/setearEstrategiaModoCalendario`,
      {
        deveui: deveui,
        estrategia: estrategia,
      },
    );
  }

  public setearMargenesAlarmas(
    deveui: string,
    margenes: {
      voltageHighLimit: number;
      voltageLowLimit: number;
      currentHighLimit: number;
      currentLowLimit: number;
    },
  ) {
    return this.http.post(`${api}/comandos/luminarias/setearMargenesAlarmas`, {
      deveui: deveui,
      margenes: margenes,
    });
  }

  public consultarEstrategiaModoCalendario(
    deveui: string,
    numeroEstrategia: number,
  ) {
    return this.http.post(
      `${api}/comandos/luminarias/consultarEstrategiaModoCalendario`,
      {
        deveui: deveui,
        numeroEstrategia: numeroEstrategia,
      },
    );
  }

  public consultarMargenesLDR(deveui: string) {
    return this.http.post(`${api}/comandos/luminarias/consultarMargenesLDR`, {
      deveui: deveui,
    });
  }

  public consultarOffsetGPS(deveui: string) {
    return this.http.post(`${api}/comandos/luminarias/consultarOffsetGPS`, {
      deveui: deveui,
    });
  }
}
